import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";

type Props = { lang: "es" | "en"; toast: (m: string) => void };

type AuditRow = {
  id: string;
  admin_email: string | null;
  action: string;
  target_email: string | null;
  target_user_id: string | null;
  params: any;
  status: string;
  error_message: string | null;
  created_at: string;
};

const T = {
  es: {
    title: "Registro de auditoría",
    desc: "Acciones administrativas y eventos de cumplimiento (exportaciones, eliminaciones).",
    refresh: "Actualizar",
    filterAction: "Filtrar acción",
    all: "Todas",
    when: "Fecha", admin: "Admin", action: "Acción", target: "Objetivo", status: "Estado", details: "Detalles",
    empty: "Sin eventos registrados.",
    deletions: "Eliminaciones de cuenta pendientes",
    scheduledFor: "Programada para",
    none: "Ninguna pendiente.",
  },
  en: {
    title: "Audit log",
    desc: "Admin actions and compliance events (exports, deletions).",
    refresh: "Refresh",
    filterAction: "Filter action",
    all: "All",
    when: "When", admin: "Admin", action: "Action", target: "Target", status: "Status", details: "Details",
    empty: "No events recorded.",
    deletions: "Pending account deletions",
    scheduledFor: "Scheduled for",
    none: "None pending.",
  },
} as const;

export function AdminAuditLog({ lang, toast }: Props) {
  const t = T[lang];
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [deletions, setDeletions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState<string>("");

  const load = async () => {
    setLoading(true);
    try {
      let q = supabase.from("admin_action_audit_log")
        .select("id,admin_email,action,target_email,target_user_id,params,status,error_message,created_at")
        .order("created_at", { ascending: false }).limit(200);
      if (actionFilter) q = q.eq("action", actionFilter);
      const { data, error } = await q;
      if (error) throw error;
      setRows((data || []) as AuditRow[]);

      const { data: dels } = await supabase.from("account_deletion_requests")
        .select("*").order("created_at", { ascending: false }).limit(100);
      setDeletions(dels || []);
    } catch (e: any) {
      toast(`❌ ${e?.message || "Error"}`);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [actionFilter]);

  const actions = Array.from(new Set(rows.map(r => r.action))).sort();

  return (
    <div className="fcard" style={{ marginTop: 10 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
        <div>
          <h3 style={{ fontFamily: "var(--fs)", fontSize: 15 }}>{t.title}</h3>
          <p style={{ fontSize: 11.5, color: "var(--tx2)" }}>{t.desc}</p>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <select value={actionFilter} onChange={e => setActionFilter(e.target.value)}
            style={{ padding: 6, fontSize: 12, border: "1px solid var(--bd2)", borderRadius: 6 }}>
            <option value="">{t.all}</option>
            {actions.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
          <button className="btn" onClick={load} disabled={loading}>{t.refresh}</button>
        </div>
      </div>

      {/* Pending deletions */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>{t.deletions}</div>
        {deletions.filter(d => d.status === "pending").length === 0 ? (
          <div style={{ fontSize: 12, color: "var(--tx3)" }}>{t.none}</div>
        ) : (
          <div style={{ display: "grid", gap: 6 }}>
            {deletions.filter(d => d.status === "pending").map(d => (
              <div key={d.id} style={{ padding: 8, border: "1px solid #fbbf24", background: "#fffbeb", borderRadius: 6, fontSize: 12 }}>
                <strong>{d.user_email || d.user_id}</strong> · {t.scheduledFor}: {new Date(d.scheduled_for).toLocaleString()}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Audit table */}
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", fontSize: 11.5, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--tx2)", borderBottom: "1px solid var(--bd2)" }}>
              <th style={{ padding: 6 }}>{t.when}</th>
              <th style={{ padding: 6 }}>{t.admin}</th>
              <th style={{ padding: 6 }}>{t.action}</th>
              <th style={{ padding: 6 }}>{t.target}</th>
              <th style={{ padding: 6 }}>{t.status}</th>
              <th style={{ padding: 6 }}>{t.details}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={6} style={{ padding: 14, textAlign: "center", color: "var(--tx3)" }}>{t.empty}</td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id} style={{ borderBottom: "1px solid var(--bd2)" }}>
                <td style={{ padding: 6, whiteSpace: "nowrap" }}>{new Date(r.created_at).toLocaleString()}</td>
                <td style={{ padding: 6 }}>{r.admin_email || "-"}</td>
                <td style={{ padding: 6, fontFamily: "monospace" }}>{r.action}</td>
                <td style={{ padding: 6 }}>{r.target_email || r.target_user_id || "-"}</td>
                <td style={{ padding: 6, color: r.status === "success" ? "#10b981" : "#ef4444" }}>{r.status}</td>
                <td style={{ padding: 6, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={JSON.stringify(r.params || r.error_message || {})}>
                  {r.error_message ? <span style={{ color: "#ef4444" }}>{r.error_message}</span> : (r.params ? JSON.stringify(r.params) : "")}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}